import React from 'react';

import { format } from 'date-fns';

import DynamicColumnsTableColumnHeader from 'components/DynamicColumnsTable/DynamicColumnsTableColumnHeader';
import { LIVING_TYPES_LABELS } from 'constants/livingTypes';
import { convertAreaByUnitSystem, convertRatePerAreaByUnitSystem, getAreaUnitSuffix } from 'utils/helpers/units';

import { COMMUNITY_COLUMN_OFFSET, WIDGET_POSTFIX, WIDGET_TYPES, WIDGET_WITHOUT_PREFIX } from '../constants';
import ValueDifference from '../ValueDifference';
import { shouldDisplayValueDifference } from '../valueDifferenceUtils';

export const valueFormatter = (
  value: number | null | undefined,
  type: number,
  showSymbol = true,
  minFractionDigits?: number,
  unitSource?: any,
  showPostfix = true
) => {
  if (value == null) return '';

  let displayValue = value;
  if ([WIDGET_TYPES.SQUARE_FOOTAGE, WIDGET_TYPES.SQUARE_FOOTAGE_GRAPH].includes(type)) {
    displayValue = convertAreaByUnitSystem(value, unitSource);
  }
  if ([WIDGET_TYPES.MARKET_RATE_SQUARE_FOOTAGE, WIDGET_TYPES.MARKET_RATE_SQUARE_FOOTAGE_GRAPH].includes(type)) {
    displayValue = convertRatePerAreaByUnitSystem(value, unitSource);
  }

  const widgetFixedPositionsMapping: Record<number, number> = {
    [WIDGET_TYPES.RATING_GRAPH]: 1,
    [WIDGET_TYPES.RATING]: 1,
    [WIDGET_TYPES.MARKET_RATE_SQUARE_FOOTAGE]: 2,
    [WIDGET_TYPES.MARKET_RATE_SQUARE_FOOTAGE_GRAPH]: 2,
    [WIDGET_TYPES.SWOT_SCORE]: 2,
  };

  const fixedPositions = widgetFixedPositionsMapping[type] ?? 0;

  const formattedValue = displayValue.toLocaleString('en-US', {
    minimumFractionDigits: minFractionDigits ?? fixedPositions,
    maximumFractionDigits: fixedPositions,
  });

  const prefix = showSymbol && !WIDGET_WITHOUT_PREFIX.includes(type) ? '$' : '';
  const postfix = showPostfix
    ? [WIDGET_TYPES.SQUARE_FOOTAGE, WIDGET_TYPES.SQUARE_FOOTAGE_GRAPH].includes(type)
      ? getAreaUnitSuffix(unitSource)
      : WIDGET_POSTFIX[type] ?? ''
    : '';

  return `${prefix}${formattedValue}${postfix}`;
};

const normalizeRowValue = (rowValue: any) => {
  if (rowValue && typeof rowValue === 'object' && 'value' in rowValue) {
    return rowValue;
  }

  return {
    value: rowValue,
    difference: undefined,
  };
};

export const getGraphWidgetTableColumns = ({
  community,
  competitors,
  filteredCompetitors,
  displayedNumberOfCompetitors,
  competitorsMinIndex,
  competitorsMaxIndex,
  type,
  valueDisplay,
  comparisonTypeEnabled,
  scrollCompetitorsLeft,
  scrollCompetitorsRight,
}: {
  community: any;
  competitors: any[];
  filteredCompetitors: any[];
  competitorsMinIndex: number;
  competitorsMaxIndex: number;
  displayedNumberOfCompetitors: number;
  type: number;
  valueDisplay?: number;
  comparisonTypeEnabled?: boolean;
  scrollCompetitorsLeft: () => void;
  scrollCompetitorsRight: () => void;
}) => {
  const communitiesList = [community, ...filteredCompetitors].filter((community) => Boolean(community));
  const communityUnitSource = community;
  const showDifference = shouldDisplayValueDifference(comparisonTypeEnabled, valueDisplay);

  const disableLeftScroll = competitorsMinIndex === 0;
  const disableRightScroll =
    competitorsMaxIndex === (competitors?.length || 0) - 1 || competitors?.length + 1 <= competitorsMaxIndex;

  const columns: any[] = [
    {
      title: '',
      render: (value: any, record: any) => <div>{record.rowName}</div>,
    },
    {
      title: communitiesList?.length ? communitiesList[0].comunity_name : '',
      render: (value: any, record: any) => {
        const { value: cellValue, difference } = normalizeRowValue(record.rowValues[0]);
        return (
          <div>
            {valueFormatter(cellValue, type, true, undefined, communityUnitSource)}
            {showDifference && (
              <ValueDifference difference={difference} widgetType={type} valueDisplay={valueDisplay} />
            )}
          </div>
        );
      },
    },
  ];

  if (communitiesList.length) {
    columns.push(
      ...communitiesList.slice(1).map((c, index: number) => ({
        title: () => (
          <DynamicColumnsTableColumnHeader
            index={index}
            community={c}
            filteredCompetitorsCount={filteredCompetitors.length}
            totalCompetitorsCount={competitors?.length ?? 0}
            scrollLeft={scrollCompetitorsLeft}
            scrollRight={scrollCompetitorsRight}
            disableLeft={disableLeftScroll}
            disableRight={disableRightScroll}
            displayedNumberOfCompetitors={displayedNumberOfCompetitors}
          />
        ),
        render: (value: any, record: any) => {
          const columnIndex = COMMUNITY_COLUMN_OFFSET + competitorsMinIndex + index;
          const { value: cellValue, difference } = normalizeRowValue(record.rowValues[columnIndex]);
          return (
            <div>
              {valueFormatter(cellValue, type, true, undefined, communityUnitSource)}
              {showDifference && (
                <ValueDifference difference={difference} widgetType={type} valueDisplay={valueDisplay} />
              )}
            </div>
          );
        },
      }))
    );
  }

  return columns;
};

const cleanText = (value: any) => {
  if (!value) return '';

  return String(value)
    .replace(/"/g, '""') // escape internal quotes
    .replace(/[\r\n]+/g, ' ') // remove newlines
    .trimEnd(); // keep leading quote if exists, remove trailing spaces only
};

export const buildIncentivesCSVData = (data: any[], selectedCommunityId: number | null) => {
  const baseColumns: Record<string, string> =
    selectedCommunityId === null
      ? {
          COMMUNITY: '',
          INCENTIVE: '',
          'CARE TYPE': '',
          ADDED: '',
        }
      : {
          INCENTIVE: '',
          'CARE TYPE': '',
          ADDED: '',
        };

  if (!data?.length) {
    return [baseColumns];
  }
  return data.map((item) => {
    const row: Record<string, string> = {};

    if (selectedCommunityId === null) {
      row['COMMUNITY'] = cleanText(item.community_name);
    }

    row['INCENTIVE'] = cleanText(item.description);
    const careTypeLabels = (item.living_types ?? [])
      .map((livingType: any) => LIVING_TYPES_LABELS[livingType])
      .filter(Boolean)
      .join(', ');

    row['CARE TYPE'] = cleanText(careTypeLabels);
    row['ADDED'] = cleanText(item.date ? format(new Date(item.date), 'MMM dd, yyyy') : '');

    return row;
  });
};
